import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useKeycloak } from '@react-keycloak/web';
import OrganizationDetails from "./OrganizationDetails";
import { getOrganizationDetails, getOrganizationMembers, removeOrganizationMember } from "../../api/organizations/organizationsApi";
import { useNavigate } from 'react-router-dom';
import { getMyDetails, getUserDetails } from "../../api/users/usersApi";
import { toast } from 'react-toastify';
import { Member, Organization, buildMember, buildOrganization } from "../common/ObjectTypes";
import { ManageOrganizationModal } from "./ManageOrganizationModal";

import RemoveMemberModal from "./RemoveMemberModal";
import { ManageMemberModal } from "../users/ManageMemberModal";
import { useTranslation } from "react-i18next";
import NotFoundPage from "../common/NotFoundPage";
import { page_size } from "../../api/apiUtils";

export function ManageOrganizationDetails() {
    const { org_id } = useParams();
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const navigate = useNavigate()
    const [orgMembers, setOrgMembers] = useState<Member[]>([]);
    const [offset, setOffset] = useState(0);
    const [organization, setOrganization] = useState<Organization>(buildOrganization());
    const [pageLoaded, setPageLoaded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [reloadOrg, setReloadOrg] = useState(true);
    const [myDetails, setMyDetails] = useState<any>({}); // this is to check superuser status
    const [currentMember, setCurrentMember] = useState<Member>(buildMember())
    const [updateModal, setUpdateModal] = useState(false);
    const [inviteMemberModal, setInviteMemberModal] = useState(false);
    const [updateMemberModal, setUpdateMemberModal] = useState(false);
    const [removeMemberModal, setRemoveMemberModal] = useState(false);
    const [memberid, setMemberid] = useState("");
    const [memberToUpdate, setMemberToUpdate] = useState<Member>(buildMember())

    useEffect(() => {
        if (!reloadOrg) return;
        const loadOrganization = async () => {
            if (keycloak.token && org_id) {
                try {
                    const tempMembers = await getOrganizationMembers(keycloak.token, org_id)
                    const tempDetails = await getOrganizationDetails(keycloak.token, org_id)
                    // A swallowed 401 resolves undefined (the session-expired modal
                    // owns that case); rendering would crash on organization.name,
                    // so keep the loading state instead.
                    if (!tempDetails) {
                        setReloadOrg(false);
                        return;
                    }
                    setOrganization(tempDetails)
                    const members = tempMembers.results;
                    setOrgMembers(members);
                    // Removing members can shrink the list past the current page
                    // (e.g. clearing the only entry on the last one).
                    setOffset((prev) => (prev >= members.length ? 0 : prev));
                    // Reset only on success (not at the top of the try): an eager reset
                    // flashes the loading state over the 404 page while a retry runs
                    setNotFound(false);
                    setPageLoaded(true);
                    setReloadOrg(false);
                } catch (err: any) {
                    setReloadOrg(false);
                    // 403 is treated like 404: the entity is not viewable by this user
                    if (err?.response?.status === 404 || err?.response?.status === 403) {
                        setNotFound(true);
                        return;
                    }
                    console.log(err);
                }
            }
        }
        loadOrganization();
    }, [keycloak.authenticated, keycloak.token, org_id, reloadOrg]);

    useEffect(() => {
        if (!pageLoaded) return;
        const loadCurrentUserDetails = async () => {
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn || !keycloak.token) return;
            try {
                const user = await getMyDetails(keycloak.token);
                if (!user || typeof user !== 'object' || !user.email || typeof user.email !== 'string') return;
                setMyDetails(user);
                for (const member of orgMembers) {
                    if (member && typeof member === 'object' && (member as any).email === user.email) {
                        setCurrentMember(member);
                    }
                }
            } catch (err) {
                console.log('Failed to load current user details:', err);
            }
        }
        loadCurrentUserDetails();
    }, [pageLoaded, orgMembers, keycloak?.authenticated, keycloak.token]);

    const handleBackArrow = () => navigate("/workspaces/")

    // The whole member list is already in memory — the endpoint has no
    // per-member filter, so the current-member lookup above needs all of it —
    // so page the table by slicing rather than by refetching.
    const membersOnPage = orgMembers.slice(offset, offset + page_size);

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
    }

    async function handleRemoveMember(member_id: string) {
        if (keycloak.token && org_id && member_id) {
            const tempMember = await getUserDetails(keycloak.token, org_id, member_id);
            await removeOrganizationMember(keycloak.token, org_id, member_id, tempMember);
            toast.success(`${t("Member removed from workspace")}`, {
                position: toast.POSITION.TOP_RIGHT
            });
            setRemoveMemberModal(false);
            setReloadOrg(true);
        }
    }

    const handleToggleOrgModal = (saved = false) => {
        if (saved) setReloadOrg(true);
        setUpdateModal(!updateModal);
    }
    const toggleOrgModalVisibility: React.MouseEventHandler<HTMLButtonElement> = () => {
        handleToggleOrgModal()
    }

    const handleToggleInviteMemberModal = (saved = false) => {
        if (saved) setReloadOrg(true);
        setInviteMemberModal(!inviteMemberModal);
    }

    async function handleToggleUpdateMemberModal(member_id?: string, saved = false) {
        if (keycloak.token && org_id && member_id) {
            const tempMember = await getUserDetails(keycloak.token, org_id, member_id);
            setMemberToUpdate(tempMember);
        }
        if (saved) setReloadOrg(true);
        setUpdateMemberModal(!updateMemberModal);
    }

    const handleToggleRemoveMemberModal = (member_id: string) => {
        setRemoveMemberModal(!removeMemberModal);
        setMemberid(member_id);
    }

    if (notFound) return <NotFoundPage />;

    return (
            <div>
                {updateModal &&
                    <ManageOrganizationModal
                        onToggleModal={handleToggleOrgModal}
                        existingOrg={organization}
                        newOrg={false}
                    />
                }
                {inviteMemberModal &&
                    <ManageMemberModal
                        onToggleModal={handleToggleInviteMemberModal}
                        existingMember={memberToUpdate}
                        newMember={true}
                        currentMember={currentMember}
                        superuser={myDetails.is_superuser}
                        orgName={organization.name}
                    />
                }
                {updateMemberModal &&
                    <ManageMemberModal
                        onToggleModal={(saved) => handleToggleUpdateMemberModal(undefined, saved)}
                        existingMember={memberToUpdate}
                        newMember={false}
                        currentMember={currentMember}
                        superuser={myDetails.is_superuser}
                        orgName={organization.name}
                    />
                }
                {removeMemberModal &&
                    <RemoveMemberModal
                        onToggleModal={handleToggleRemoveMemberModal}
                        onRemoveMember={handleRemoveMember}
                        memberid={memberid}
                        currentMember={currentMember}
                        superuser={myDetails.is_superuser}
                        orgName={organization.name}
                    />
                }
                <OrganizationDetails
                    orgMembers={membersOnPage}
                    totalCount={orgMembers.length}
                    offset={offset}
                    take={page_size}
                    onPageClicked={handlePageClick}
                    organization={organization}
                    onToggleModal={toggleOrgModalVisibility}
                    onToggleRemoveMemberModal={handleToggleRemoveMemberModal}
                    onBackArrow={handleBackArrow}
                    onClickInvite={() => handleToggleInviteMemberModal()}
                    onClickMember={handleToggleUpdateMemberModal}
                    currentMember={currentMember}
                    superuser={myDetails.is_superuser}
                    pageLoaded={pageLoaded}
                />
            </div>
        )
}